import { useEffect, useRef, useState } from "react";
import { ImportButton } from "../components/ImportDialog";
import { PreferredAgentsField } from "../components/PreferredAgents";
import { http, formatApiError, askDelete, delTitle } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { Plus, Pencil, Trash2, X, MapPin, Briefcase } from "lucide-react";
import RatesMatrix, { serializeRatesMatrix, deserializeRatesMatrix } from "../components/RatesMatrix";
import { SiteActivitiesPicker } from "../components/NewActivity";
import { useActivities } from "../lib/activities";
import { surchargeMultipliers } from "../components/ClientSurcharges";
import { SearchSelect } from "../components/SearchSelect";
import { Suggest } from "../components/Suggest";
import { SiteMaterialPanel } from "../components/equipment/Panels";
import { BroadcastButton } from "../components/BroadcastMessage";
import { toast } from "sonner";
import { BanConflictsDialog } from "../components/SiteBanDialog";

export default function Sites() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const { lastEvent } = useRealtime();
  const load = async () => {
    try {
      const [s, c] = await Promise.all([http.get("/sites"), http.get("/clients")]);
      setItems(s.data);
      setClients(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (lastEvent?.event === "site_changed" || lastEvent?.event === "client_changed") load();
  }, [lastEvent]);

  const clientById = (id) => clients.find((c) => c.id === id);

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="sites-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Sites</h1>
          <p className="text-sm text-slate-600">{items.length} sites clients</p>
        </div>
        <div className="flex gap-2"><BroadcastButton initial={{ mode: "my_sites" }} className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-500 bg-white px-4 py-2 rounded-md text-sm font-semibold" /><ImportButton kind="sites" onDone={load} />
        <button data-testid="add-site" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-md text-sm font-semibold"><Plus className="h-4 w-4"/> Nouveau site</button></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => {
          const c = clientById(s.client_id);
          return (
            <div key={s.id} className="border border-slate-200 bg-white rounded-md p-5 flex flex-col gap-3 hover:border-slate-300">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-md grid place-items-center text-white shrink-0" style={{background: s.color || "#0284c7"}}><MapPin className="h-5 w-5"/></div>
                  <div className="min-w-0">
                    <div className="font-display text-lg font-bold truncate">{s.name}</div>
                    <div className="text-xs text-slate-500 truncate">{s.city || "—"}</div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`edit-site-${s.id}`} onClick={()=>setEditing(s)} className="p-1.5 rounded hover:bg-slate-100"><Pencil className="h-4 w-4"/></button>
                  <button data-testid={`delete-site-${s.id}`} onClick={async()=>{ if(askDelete("Supprimer ?")){ await http.delete(`/sites/${s.id}`); load();}}} title={delTitle()} className="p-1.5 rounded hover:bg-rose-50"><Trash2 className="h-4 w-4 text-rose-600"/></button>
                </div>
              </div>
              <div className="text-sm text-slate-600">{s.address || "Adresse non renseignée"}</div>
              {c && (
                <div className="flex items-center gap-2 text-xs bg-indigo-50 border border-indigo-100 rounded px-2 py-1.5">
                  <Briefcase className="h-3.5 w-3.5 text-indigo-700"/>
                  <span className="font-bold text-indigo-900 truncate">{c.name}</span>
                </div>
              )}
              {(s.contact_name || s.contact_phone) && (
                <div className="border-t border-slate-100 pt-3 text-xs">
                  <div className="font-semibold text-slate-700">{s.contact_name}</div>
                  <div className="text-slate-500">{s.contact_phone}</div>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <div className="text-slate-500 text-sm col-span-full text-center py-10 border border-dashed border-slate-200 rounded-md">Aucun site. Cliquez sur "Nouveau site".</div>}
      </div>

      {editing && <SiteModal value={editing} clients={clients} onClose={()=>setEditing(null)} onSaved={(c) => { load(); if (c?.length) setConflicts(c); }}/>}
      {conflicts && <BanConflictsDialog items={conflicts} onClose={() => setConflicts(null)} />}
    </div>
  );
}

export function SiteModal({ value, clients, onClose, onSaved }) {
  const acts = useActivities();
  const [v, setV] = useState({
    name:"", address:"", postal_code:"", city:"", instructions:"", contact_name:"", contact_phone:"", alarm_account:"", visitors_email:"",
    color:"#0284c7", lat:null, lon:null, radius_m:200,
    client_id: null,
    ...value,
  });
  const [rates, setRates] = useState(() => deserializeRatesMatrix(value?.rates_override));
  const [manualCells, setManualCells] = useState(() => new Set());
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const linkedClient = clients.find((c) => c.id === v.client_id);
  const [company, setCompany] = useState(null);
  useEffect(() => { http.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {}); }, []);
  const inheritedRates = linkedClient?.default_rates || null;
  const [geo, setGeo] = useState(null);
  const geoQ = (x) => [x.address, x.postal_code, x.city].map((s) => (s || "").trim()).filter(Boolean).join(" ");
  const lastQ = useRef(value?.lat != null ? geoQ(v) : "");
  const geocode = async () => {
    const q = geoQ(v);
    if (!v.address?.trim() || !(v.postal_code || v.city?.trim()) || q === lastQ.current) return;
    lastQ.current = q;
    try {
      const { data } = await http.get("/public/address-search", { params: { q } });
      const r = data[0];
      if (!r) return setGeo({ ok: false, msg: "Adresse introuvable : saisissez les coordonnées à la main" });
      setV((x) => ({ ...x, lat: Number(r.lat.toFixed(6)), lon: Number(r.lon.toFixed(6)), postal_code: x.postal_code || r.postal_code, city: x.city || r.city }));
      setGeo({ ok: true, msg: `Coordonnées remplies d'après « ${r.label} »` });
    } catch { setGeo({ ok: false, msg: "Recherche des coordonnées indisponible" }); }
  };
  const pickAddr = (r) => {
    const nv = { ...v, address: r.street || r.label, postal_code: r.postal_code || v.postal_code, city: r.city || v.city, lat: Number(r.lat.toFixed(6)), lon: Number(r.lon.toFixed(6)) };
    lastQ.current = geoQ(nv);
    setV(nv);
    setGeo({ ok: true, msg: `Adresse et coordonnées remplies d'après « ${r.label} »` });
  };

  const useMyPos = () => {
    if (!navigator.geolocation) return alert("Géolocalisation indisponible");
    navigator.geolocation.getCurrentPosition(
      (p) => setV({ ...v, lat: p.coords.latitude, lon: p.coords.longitude }),
      (e) => alert("Échec géolocalisation : " + e.message),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };
  const setRate = (activity, shift, val) => {
    setRates((prev) => {
      const next = { ...prev, [activity]: { ...prev[activity], [shift]: val } };
      if (shift === "regular" && val !== "" && !Number.isNaN(Number(val))) {
        const base = Number(val);
        const multipliers = surchargeMultipliers(linkedClient, company);
        const row = next[activity];
        for (const k of ["night", "sunday", "holiday"]) {
          if (!manualCells.has(`${activity}:${k}`)) {
            row[k] = (base * multipliers[k]).toFixed(3);
          }
        }
      }
      return next;
    });
    if (shift === "regular") {
      setManualCells((prev) => {
        const next = new Set(prev);
        for (const k of ["night", "sunday", "holiday"]) next.delete(`${activity}:${k}`);
        return next;
      });
    } else {
      setManualCells((prev) => {
        const next = new Set(prev);
        const key = `${activity}:${shift}`;
        if (val === "" || val === null || val === undefined) next.delete(key);
        else next.add(key);
        return next;
      });
    }
  };

  const save = async () => {
    if (saving) return;
    if (!(v.name || "").trim()) { setErr("Le nom du site est obligatoire"); return; }
    setSaving(true);
    try {
      const body = {
        name: v.name,
        address: v.address,
        postal_code: v.postal_code || "",
        instructions: v.instructions || "",
        city: v.city,
        contact_name: v.contact_name,
        contact_phone: v.contact_phone,
        alarm_account: (v.alarm_account || "").trim(),
        visitors_email: (v.visitors_email || "").trim(),
        color: v.color,
        lat: v.lat != null ? Number(v.lat) : null,
        lon: v.lon != null ? Number(v.lon) : null,
        radius_m: v.radius_m ? parseInt(v.radius_m) : null,
        client_id: v.client_id || null,
        rates_override: serializeRatesMatrix(rates),
        preferred_agent_ids: v.preferred_agent_ids || [],
        banned_agent_ids: v.banned_agent_ids || [],
        activity_codes: v.activity_codes || [],
      };
      const r = v.id ? await http.put(`/sites/${v.id}`, body) : await http.post("/sites", body);
      toast.success(v.id ? "Site enregistré" : "Site créé");
      onSaved(r.data?.ban_conflicts, r.data); onClose();
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-2xl border border-slate-200 animate-fade-in-up max-h-[95vh] overflow-auto" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-bold">{v.id?"Modifier":"Nouveau"} site</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4"/></button>
        </div>
        <div className="p-5 space-y-4">
          <L label="Nom *"><input data-testid="site-name" className="input" value={v.name} onChange={(e)=>setV({...v,name:e.target.value})}/></L>
          <L label="Client lié">
            <SearchSelect
              data-testid="site-client"
              className="input"
              value={v.client_id || ""}
              onChange={(e) => setV({ ...v, client_id: e.target.value || null })}
            >
              <option value="">— Aucun client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </SearchSelect>
          </L>
          <L label="Adresse"><Suggest testid="site-address" inputClass="input" placeholder="Commencez à taper l'adresse…" value={v.address || ""} minChars={4}
            fetcher={(q) => http.get("/public/address-search", { params: { q } }).then((r) => r.data)} onPick={pickAddr} onBlur={geocode}
            onChange={(address) => setV((x) => ({ ...x, address }))} render={(a) => <span>{a.label}</span>} /></L>
          <div className="grid grid-cols-3 gap-3">
            <L label="Code postal"><input data-testid="site-postal-code" className="input" maxLength={5} value={v.postal_code || ""} onChange={(e)=>setV({...v,postal_code:e.target.value.replace(/\D/g, "")})} onBlur={geocode} placeholder="75008"/></L>
            <L label="Ville"><input data-testid="site-city" className="input" value={v.city} onChange={(e)=>setV({...v,city:e.target.value})} onBlur={geocode}/></L>
            <L label="Couleur"><input type="color" className="input h-10" value={v.color} onChange={(e)=>setV({...v,color:e.target.value})}/></L>
          </div>
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Géofencing (pointage GPS)</div>
            <div className="grid grid-cols-3 gap-2">
              <L label="Latitude"><input type="number" step="0.000001" data-testid="site-lat" className="input" value={v.lat ?? ""} onChange={(e)=>setV({...v, lat: e.target.value === "" ? null : parseFloat(e.target.value)})}/></L>
              <L label="Longitude"><input type="number" step="0.000001" data-testid="site-lon" className="input" value={v.lon ?? ""} onChange={(e)=>setV({...v, lon: e.target.value === "" ? null : parseFloat(e.target.value)})}/></L>
              <L label="Rayon (m)"><input type="number" data-testid="site-radius" className="input" value={v.radius_m ?? 200} onChange={(e)=>setV({...v, radius_m: e.target.value === "" ? null : parseInt(e.target.value)})}/></L>
            </div>
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={useMyPos} className="text-xs font-semibold border border-slate-300 bg-white rounded px-3 py-1.5 hover:bg-slate-50">📍 Utiliser ma position</button>
              {geo && <span data-testid="site-geocode-status" className={`text-xs self-center ${geo.ok ? "text-emerald-700" : "text-amber-700"}`}>{geo.msg}</span>}
              {v.lat && v.lon && <a href={`https://www.openstreetmap.org/?mlat=${v.lat}&mlon=${v.lon}#map=18/${v.lat}/${v.lon}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-sky-700 hover:underline self-center">Voir sur la carte ↗</a>}
            </div>
            <p className="text-[10px] text-slate-500 mt-2">Si défini, les pointages des agents seront comparés à cette position. Au-delà du rayon, l'arrivée/départ sera marquée "hors zone".</p>
          </div>

          <SiteActivitiesPicker acts={acts} value={v.activity_codes} onChange={(c) => setV((x) => ({ ...x, activity_codes: c }))} />

          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Tarifs spécifiques au site (€/h HT)</div>
              {linkedClient && (
                <span className="text-[10px] text-indigo-700 font-semibold">Tarifs hérités de {linkedClient.name} en placeholder</span>
              )}
            </div>
            <RatesMatrix value={rates} placeholders={inheritedRates} onChange={setRate} testIdPrefix="site-rate" />
            <p className="text-[10px] text-slate-500 mt-2">Laissez une cellule vide pour appliquer le tarif du client. Sinon, c'est ce tarif qui prévaut pour ce site.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <L label="Contact nom"><input className="input" value={v.contact_name} onChange={(e)=>setV({...v,contact_name:e.target.value})}/></L>
            <L label="Contact tél."><input className="input" value={v.contact_phone} onChange={(e)=>setV({...v,contact_phone:e.target.value})}/></L>
            <L label="E-mail des agents du site (annonces visiteurs)"><input data-testid="site-visitors-email" type="email" className="input" value={v.visitors_email || ""} placeholder="ex. poste.securite@client.fr" onChange={(e)=>setV({...v,visitors_email:e.target.value})}/></L>
            <L label="N° d'abonné télésurveillance"><input data-testid="site-alarm-account" className="input font-mono" value={v.alarm_account || ""} placeholder="Fourni par votre centre (sinon n° de la société)" onChange={(e)=>setV({...v,alarm_account:e.target.value.toUpperCase()})}/></L>
          </div>
          <L label="Consignes particulières (visibles par les agents sur leur planning)"><textarea data-testid="site-instructions" className="input h-24" value={v.instructions || ""} onChange={(e)=>setV({...v,instructions:e.target.value})} placeholder="Ex. : badge à récupérer au PC sécurité, tenue noire, ronde toutes les 2 h…"/></L>
          <PreferredAgentsField value={v.preferred_agent_ids || []} onChange={(ids) => setV({ ...v, preferred_agent_ids: ids })} />
          <PreferredAgentsField variant="banned" value={v.banned_agent_ids || []} onChange={(ids) => setV({ ...v, banned_agent_ids: ids })} />
          {v.id && <SiteMaterialPanel siteId={v.id} />}
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
          <button data-testid="site-save" onClick={save} disabled={saving} className="disabled:opacity-50 px-4 py-2 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-md">Enregistrer</button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}
function L({label, children}) { return <label className="block"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>; }
